import { useCallback, useEffect } from 'react'

import { useModalInstance } from '@context/ModalInstanceContext'
import { useModals } from '@context/ModalsContext'

type PlayExit = () => Promise<unknown> | void

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms))

export const MODAL_EXIT_MS = 220
export const EXIT_BUFFER_MULTIPLIER = 1.5

export const useAnimatedModalClose = (playExit: PlayExit) => {
  const { startCloseModal, finalizeClose, registerRequestClose } = useModals()
  const { id, canClose } = useModalInstance()

  const watchdogMs = Math.max(0, Math.ceil(MODAL_EXIT_MS * EXIT_BUFFER_MULTIPLIER))

  const requestClose = useCallback(async () => {
    if (!canClose) return

    const started = startCloseModal(id)
    if (!started) return

    const exitPromise = Promise.resolve()
      .then(() => playExit())
      .catch(() => undefined)

    await Promise.all([Promise.race([exitPromise, sleep(watchdogMs)]), sleep(watchdogMs)]).catch(
      () => undefined
    )
    await finalizeClose(id)
  }, [canClose, finalizeClose, id, playExit, startCloseModal, watchdogMs])

  useEffect(() => {
    registerRequestClose(id, requestClose)
    return () => registerRequestClose(id, null)
  }, [id, registerRequestClose, requestClose])

  return requestClose
}
